import { z } from "zod";
import { OrganizationRoleSchema } from "./organization";

export const JOB_STATUSES = ["active", "complete", "archived"] as const;
export const DEFAULT_GEOFENCE_RADIUS_M = 100;
export const MIN_GEOFENCE_RADIUS_M = 25;
export const MAX_GEOFENCE_RADIUS_M = 500;

const geofenceRadiusField = z
  .number()
  .int()
  .min(MIN_GEOFENCE_RADIUS_M)
  .max(MAX_GEOFENCE_RADIUS_M);

export const JobStatusSchema = z.enum(JOB_STATUSES);
export type JobStatus = z.infer<typeof JobStatusSchema>;

export const JobCapabilitiesSchema = z.object({
  role: OrganizationRoleSchema,
  canCapturePhotos: z.boolean(),
  canCreateJobs: z.boolean().optional().default(false),
  canManageJobs: z.boolean(),
  canViewDeleted: z.boolean().optional().default(false),
  canManageAllPhotos: z.boolean(),
  canManageOwnPhotos: z.boolean(),
});

export type JobCapabilities = z.infer<typeof JobCapabilitiesSchema>;

export const JobSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  ownerId: z.string().nullable(),
  organizationId: z.string().nullable().optional(),
  lat: z.number(),
  lng: z.number(),
  altitudeM: z.number().nullable(),
  address: z.string().nullable(),
  geofenceRadiusM: geofenceRadiusField,
  status: JobStatusSchema,
  deletedAt: z.string().datetime().nullable().optional(),
  photoCount: z.number().int().optional(),
  videoCount: z.number().int().optional(),
  ownerName: z.string().optional(),
  capabilities: JobCapabilitiesSchema,
  createdAt: z.string().datetime(),
  createdAtDisplay: z.string().optional(),
});

export type Job = z.infer<typeof JobSchema>;

export const CreateJobSchema = z.object({
  name: z.string().min(1).max(200),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  altitudeM: z.number().optional(),
  address: z.string().optional(),
  geofenceRadiusM: geofenceRadiusField.default(DEFAULT_GEOFENCE_RADIUS_M),
});

// z.input gives the "before parse" type — geofenceRadiusM is optional because it has a default
export type CreateJobInput = z.input<typeof CreateJobSchema>;

export const UpdateJobSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional(),
  geofenceRadiusM: geofenceRadiusField.optional(),
  address: z.string().max(500).optional(),
  status: JobStatusSchema.optional(),
});

export type UpdateJobInput = z.infer<typeof UpdateJobSchema>;

export const GeofenceMatchSchema = z.object({
  jobId: z.string().uuid().nullable(),
  needsJobAssignment: z.boolean(),
  matchedJob: JobSchema.nullable(),
  nearbyJobs: z
    .array(
      z.object({
        id: z.string().uuid(),
        name: z.string(),
        address: z.string().nullable(),
        horizontalDistanceM: z.number(),
        verticalDistanceM: z.number().nullable(),
        isVerticalMatch: z.boolean(),
        capabilities: JobCapabilitiesSchema,
      }),
    )
    .default([]),
});

export type GeofenceMatch = z.infer<typeof GeofenceMatchSchema>;

export function getJobCapabilities(
  job: Pick<Job, "capabilities"> | null | undefined,
): JobCapabilities {
  return (
    job?.capabilities ?? {
      role: "viewer",
      canCapturePhotos: false,
      canCreateJobs: false,
      canManageJobs: false,
      canViewDeleted: false,
      canManageAllPhotos: false,
      canManageOwnPhotos: false,
    }
  );
}
